// src/web/client/src/components/analytics/VIPCustomers.jsx
// VIP Customers Component - Pro Plan Feature

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { safeApi, isFeatureError } from '@/utils/api';
import { storage } from '@/utils/storage';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/components/ui/use-toast';

/**
 * VIPCustomers Component
 * 
 * Displays and manages VIP customers based on purchase history and engagement.
 * Can be used standalone or embedded in other pages (Dashboard, Analytics).
 * 
 * Props:
 * - compact: boolean - If true, shows a simplified view (for dashboard widgets)
 * - limit: number - Max number of customers to show (default: 10)
 * - showActions: boolean - Show action buttons (default: true)
 * - onCustomerSelect: function - Callback when customer is selected
 */
export default function VIPCustomers({ 
  compact = false, 
  limit = 10, 
  showActions = true,
  onCustomerSelect 
}) {
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [vipData, setVipData] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showMessageModal, setShowMessageModal] = useState(false);

  // Fetch VIP data
  useEffect(() => {
    fetchVIPCustomers();
  }, []);

  const fetchVIPCustomers = async () => {
    setLoading(true);
    try {
      const data = await safeApi.get('/analytics/vip');
      
      if (isFeatureError(data)) {
        setError(data);
        return;
      }
      
      setVipData(data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch VIP customers:', err);
      setError({ message: 'Failed to load VIP customers' });
    } finally {
      setLoading(false);
    }
  };

  // Fetch detailed VIP report
  const fetchVIPReport = async () => {
    try {
      const report = await safeApi.get('/analytics/vip/report');
      if (!isFeatureError(report)) {
        return report;
      }
    } catch (err) {
      console.error('Failed to fetch VIP report:', err);
    }
    return null;
  };

  // Handle customer selection
  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    if (onCustomerSelect) {
      onCustomerSelect(customer);
    }
  };

  // Open message modal
  const handleSendMessage = (customer) => {
    setSelectedCustomer(customer);
    setShowMessageModal(true);
  };

  // Generate coupon for customer
  const handleGenerateCoupon = async (customer) => {
    try {
      const result = await safeApi.post('/analytics/coupon/generate', {
        customer_email: customer.email || customer.customer_email,
        shop_domain: storage.getShopDomain()
      });
      
      if (result && result.success && result.data) {
        alert(`Coupon generated: ${result.data.code} (${result.data.discount_percentage}% off)`);
      } else {
        alert('Failed to generate coupon: ' + (result?.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Failed to generate coupon:', err);
      alert('Failed to generate coupon');
    }
  };

  // Feature not available
  if (error && error.code === 'FEATURE_NOT_AVAILABLE') {
    return (
      <div className={`bg-white rounded-lg shadow ${compact ? 'p-4' : 'p-6'}`}>
        <div className="text-center py-6">
          <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">VIP Customers</h3>
          <p className="text-sm text-gray-500 mb-4">Available on Pro plan</p>
          <button
            onClick={() => navigate('/billing')}
            className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700"
          >
            Upgrade to Pro
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className={`bg-white rounded-lg shadow ${compact ? 'p-4' : 'p-6'}`}>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
          <span className="ml-2 text-gray-500">Loading VIP customers...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`bg-white rounded-lg shadow ${compact ? 'p-4' : 'p-6'}`}>
        <div className="text-center py-6">
          <p className="text-red-500">{error.message || 'Failed to load data'}</p>
          <button
            onClick={fetchVIPCustomers}
            className="mt-2 text-purple-600 hover:text-purple-800 text-sm"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  const customers = (vipData?.data?.customers || []).map(c => ({
    ...c,
    name: c.customer_name,
    email: c.customer_email,
    total_spent: c.metrics?.total_spent || 0,
    order_count: c.metrics?.order_count || 0,
    last_order_date: c.metrics?.days_since_last_order !== undefined 
      ? new Date(Date.now() - c.metrics.days_since_last_order * 24 * 60 * 60 * 1000)
      : null
  }));

  const summary = {
    total_vip_revenue: vipData?.data?.customers?.reduce((sum, c) => sum + (c.metrics?.total_spent || 0), 0) || 0,
    avg_order_value: vipData?.data?.customers?.length > 0 
      ? vipData.data.customers.reduce((sum, c) => sum + (c.metrics?.average_order_value || 0), 0) / vipData.data.customers.length 
      : 0,
    vip_percentage: vipData?.data?.total_vip_customers || 0
  };

  // Compact view (for dashboard widget)
  if (compact) {
    return (
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900 flex items-center">
            <span className="mr-2">⭐</span>
            VIP Customers
          </h3>
          <button
            onClick={() => navigate('/analytics?tab=vip')}
            className="text-sm text-purple-600 hover:text-purple-800"
          >
            View all
          </button>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-purple-50 rounded-lg p-3">
            <p className="text-2xl font-bold text-purple-600">{customers.length}</p>
            <p className="text-xs text-gray-500">VIP Customers</p>
          </div>
          <div className="bg-green-50 rounded-lg p-3">
            <p className="text-2xl font-bold text-green-600">
              ${(summary.total_vip_revenue || 0).toLocaleString()}
            </p>
            <p className="text-xs text-gray-500">VIP Revenue</p>
          </div>
        </div>

        {/* Top 3 VIPs */}
        <div className="space-y-2">
          {customers.slice(0, 3).map((customer, i) => (
            <div 
              key={i} 
              className="flex items-center justify-between p-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
              onClick={() => handleSelectCustomer(customer)}
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                  <span className="text-purple-600 text-sm font-medium">
                    {(customer.name || customer.email || '?')[0].toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{customer.name || 'Unknown'}</p>
                  <p className="text-xs text-gray-500">${(customer.total_spent || 0).toLocaleString()}</p>
                </div>
              </div>
              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                Score: {customer.vip_score || 0}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Full view
  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="p-6 border-b">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <span className="mr-2">⭐</span>
              VIP Customers
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Your most valuable customers based on purchase history and engagement
            </p>
          </div>
          <button
            onClick={fetchVIPCustomers}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      {/* Overlap fix, view layer only (values + VIP logic untouched). The old
          fixed grid-cols-4 + text-3xl collided in the narrow Shopify embedded
          iframe: a 7-figure "Total Revenue" is a single unbreakable token, so a
          too-thin column doesn't clip — it pushes the whole card wider. So we
          only go 4-up once columns are provably wide enough (md), stay 2-up
          below that, and only grow the number to text-3xl at xl where there's
          ample room. No value overflows at any realistic width. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6 bg-gray-50 border-b">
        <div className="text-center">
          <p className="text-2xl xl:text-3xl font-bold text-purple-600">{customers.length}</p>
          <p className="text-sm text-gray-500">VIP Customers</p>
        </div>
        <div className="text-center">
          <p className="text-2xl xl:text-3xl font-bold text-green-600">
            ${(summary.total_vip_revenue || 0).toLocaleString()}
          </p>
          <p className="text-sm text-gray-500">Total Revenue</p>
        </div>
        <div className="text-center">
          <p className="text-2xl xl:text-3xl font-bold text-blue-600">
            ${(summary.avg_order_value || 0).toFixed(0)}
          </p>
          <p className="text-sm text-gray-500">Avg Order Value</p>
        </div>
        <div className="text-center">
          <p className="text-2xl xl:text-3xl font-bold text-orange-600">
            {summary.vip_percentage || 0}%
          </p>
          <p className="text-sm text-gray-500">Of All Customers</p>
        </div>
      </div>

      {/* Customers Table */}
      <div className="overflow-x-auto">
        {customers.length > 0 ? (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Spent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Orders
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg Order
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  VIP Score
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Order
                </th>
                {showActions && (
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {customers.slice(0, limit).map((customer, i) => (
                <tr 
                  key={i} 
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleSelectCustomer(customer)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                        <span className="text-purple-600 font-medium">
                          {(customer.name || customer.email || '?')[0].toUpperCase()}
                        </span>
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-900">
                          {customer.name || 'Unknown'}
                        </p>
                        <p className="text-sm text-gray-500">{customer.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-semibold text-green-600">
                      ${(customer.total_spent || 0).toLocaleString()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {customer.order_count || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ${((customer.total_spent || 0) / (customer.order_count || 1)).toFixed(0)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <VIPScoreBadge score={customer.vip_score || 0} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {customer.last_order_date 
                      ? new Date(customer.last_order_date).toLocaleDateString()
                      : 'N/A'
                    }
                  </td>
                  {showActions && (
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => handleSendMessage(customer)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Send Message"
                        >
                          💬
                        </button>
                      {/*  <button
                          onClick={() => handleGenerateCoupon(customer)}
                          className="text-green-600 hover:text-green-900"
                          title="Generate Coupon"
                        >
                          🎟️
                        </button>*/}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500">No VIP customers identified yet</p>
            <p className="text-sm text-gray-400 mt-1">
              VIP status is determined by purchase history and engagement
            </p>
          </div>
        )}
      </div>

      {/* Footer */}
      {customers.length > limit && (
        <div className="p-4 border-t text-center">
          <button
            onClick={() => navigate('/analytics?tab=vip')}
            className="text-purple-600 hover:text-purple-800 text-sm font-medium"
          >
            View all {customers.length} VIP customers →
          </button>
        </div>
      )}

      {/* Message Modal */}
      {showMessageModal && selectedCustomer && (
        <SendMessageModal
          customer={selectedCustomer}
          onClose={() => {
            setShowMessageModal(false);
            setSelectedCustomer(null);
          }}
        />
      )}
    </div>
  );
}

// ============================================
// HELPER COMPONENTS
// ============================================

/**
 * VIP Score Badge
 */
function VIPScoreBadge({ score }) {
  let bgColor = 'bg-gray-100';
  let textColor = 'text-gray-700';
  let label = 'Standard';

  if (score >= 90) {
    bgColor = 'bg-purple-100';
    textColor = 'text-purple-700';
    label = 'Diamond';
  } else if (score >= 70) {
    bgColor = 'bg-yellow-100';
    textColor = 'text-yellow-700';
    label = 'Gold';
  } else if (score >= 50) {
    bgColor = 'bg-gray-200';
    textColor = 'text-gray-700';
    label = 'Silver';
  }

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
      {score} - {label}
    </span>
  );
}

/**
 * Send Message Modal
 */
function SendMessageModal({ customer, onClose }) {
  const { toast } = useToast();
  const [message, setMessage] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [templates, setTemplates] = useState([]);
  const [sending, setSending] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  // Confirm-before-send state. Without this the merchant click → real
  // Resend send → -1 quota with no chance to back out. PR #48 audit
  // confirmed /api/send/message counts; PR #47 added the same warning to
  // TestMessageDialog. This is the same pattern, for the VIP surface.
  const [confirmOpen, setConfirmOpen] = useState(false);

  const customerLabel =
    customer.name || customer.customer_name || customer.email || customer.customer_email;
  const recipientEmail = customer.email || customer.customer_email;

  // Fetch templates on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const data = await safeApi.get('/templates');
        if (Array.isArray(data)) {
          setTemplates(data);
          if (data.length > 0) {
            setSelectedTemplate(data[0]._id);
          }
        }
      } catch (err) {
        console.error('Failed to fetch templates:', err);
      } finally {
        setLoadingTemplates(false);
      }
    };
    fetchTemplates();
  }, []);

  // Opens the confirm — does NOT send yet. We don't want a Send click to
  // immediately spend a quota slot when the merchant might have just
  // clicked the wrong button or picked the wrong template.
  const handleSendClick = () => {
    if (!selectedTemplate) {
      toast({
        title: 'Pick a template',
        description: 'Choose a template before sending.',
        variant: 'destructive',
      });
      return;
    }
    if (!recipientEmail) {
      toast({
        title: 'Missing email',
        description: "This customer has no email address on file.",
        variant: 'destructive',
      });
      return;
    }
    setConfirmOpen(true);
  };

  const handleSendConfirmed = async () => {
    setConfirmOpen(false);
    setSending(true);
    try {
      const result = await safeApi.post('/send/message', {
        shop_domain: storage.getShopDomain(),
        template_id: selectedTemplate,
        customer_email: recipientEmail,
        customer_name: customer.name || customer.customer_name,
        variables: {
          message: message
        }
      });

      if (result && result.success) {
        // Backend can return success:true but email.success:false when
        // Resend rejects the recipient (e.g. invalid domain). Surface
        // that as an error so the merchant doesn't think a doomed send
        // succeeded.
        if (result.email && result.email.success === false) {
          toast({
            title: 'Send failed',
            description: result.email.error || 'Email provider rejected the send',
            variant: 'destructive',
          });
          return;
        }
        toast({
          title: 'Message sent',
          description: `Sent to ${recipientEmail}. Uses 1 message from your plan.`,
        });
        onClose();
      } else {
        toast({
          title: 'Send failed',
          description: result?.error || 'Unknown error',
          variant: 'destructive',
        });
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      toast({
        title: 'Send failed',
        description: err?.message || 'Could not send message',
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
      
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Send Message to {customer.name || customer.customer_name || customer.email}
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Template
            </label>
            {loadingTemplates ? (
              <p className="text-sm text-gray-500">Loading templates...</p>
            ) : templates.length > 0 ? (
              <select
                value={selectedTemplate}
                onChange={(e) => setSelectedTemplate(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
              >
                {templates.map((t) => (
                  <option key={t._id} value={t._id}>
                    {t.name} ({t.type})
                  </option>
                ))}
              </select>
            ) : (
              <p className="text-sm text-red-500">No templates found. Please create a template first.</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Additional Note (optional)
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
              placeholder="Add a personal note..."
            />
          </div>
        </div>
        
        {/*
          Quota note shown inline before the Send button so the merchant
          sees the cost of this action before they reach for the click —
          not just inside the confirm dialog that opens after.
        */}
        <div className="mt-4 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          Sending a message uses 1 message from your monthly plan.
        </div>

        <div className="flex gap-3 mt-4">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSendClick}
            disabled={sending || !selectedTemplate || loadingTemplates}
            className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            {sending ? 'Sending...' : 'Send Message'}
          </button>
        </div>
      </div>

      {/*
        Confirm-before-send. AlertDialog is the right primitive here vs.
        the outer overlay-click-to-close — a real Resend send is non-
        reversible and burns 1 quota slot, so it must require a deliberate
        click rather than dismissing on a stray overlay tap.
      */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send message to {customerLabel}?</AlertDialogTitle>
            <AlertDialogDescription>
              We'll send a real email to{' '}
              <span className="font-medium">{recipientEmail}</span> using your
              configured email provider. This uses{' '}
              <span className="font-medium">1 message</span> from your monthly
              plan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSendConfirmed}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Send
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Export for use in Dashboard widget
export { VIPScoreBadge };